import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import type { WebsiteHeaderLayout_VideoBackground } from '@shared/types/WebsiteHeaderSettings.types';
import { Box, Flex, styled, useToast } from '@withjoy/joykit';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { AnimatedImageBox } from '@apps/guest/packages/layout-engine/widgets/Custom/components/PhotoTrio/PhotoTrio.styles';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import type { EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventPageType } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import type { GuestSiteEventDesign } from '../../../../layout.types';
import { addError } from '@shared/utils/logger';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { buildVimeoBackgroundEmbedUrl, extractYoutubeVideoId, isVimeoUrl, isYoutubeUrl } from '@shared/utils/video';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { YoutubeVideoContainer } from '../components/YoutubeVideoContainer/YoutubeVideoContainer';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useIsCompactBrannanHeader } from '../hooks/useIsCompactBrannanHeader';
import { shouldHideStandaloneVideo } from './shouldHideStandaloneVideo';
import { shouldOverlayStandaloneNav } from './standaloneNavOverlay';
import { useVimeoPlaybackSignal } from '@shared/hooks/useVimeoPlaybackSignal';
import { useIsAdminGuestSitePreview } from '@apps/guest/shared/hooks/useIsAdminGuestSitePreview';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { VimeoStallDetection } from '@shared/hooks/useVimeoPlaybackSignal.types';
import { VideoStallPlayButton } from '../components/VideoStallPlayButton/VideoStallPlayButton';
import type { VideoStallButtonPlacement } from '../components/VideoStallPlayButton/VideoStallPlayButton.types';
import { EVENT_DISPLAY_NAME_MAX_LENGTH } from '@shared/constants/textLimits';

export type VideoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_VideoBackground;
    showSkeleton?: boolean;
    showPlaceholder?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
  } & EventNameNavForStandaloneHeaderProps
>;

export type VideoBackgroundRendererProps = Readonly<{
  videoUrl?: string;
  showPlaceholder?: boolean;
  skipAnimation?: boolean;
  thumbnailUrl?: string;
  height: string;
  isCompactView: boolean;
  isMobile: boolean;
  currentPageType: EventPageType | undefined;
  pageSlug: string | undefined;
  handleEditVideoClick: () => void;
  editActionVerticalAlign?: 'center' | 'top' | 'bottom';
  /** Frame corner for the tap-to-play button shown when Vimeo autoplay stalls; pick one the layout's content leaves free. */
  stallButtonPlacement?: VideoStallButtonPlacement;
  children: React.ReactNode;
}>;

/* Sits above art sprites (docked+1) that live inside FrameOuter, which is a sibling
   rendered after StandaloneWebsiteHeader in the DOM. */
const VideoBackgroundOuter = styled(Box)`
  z-index: ${({ theme }) => theme.zIndices.docked + 2};
`;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.videoBackground.aspectRatio as number;

// One surface name covers all three header layouts; New Relic's pageUrl and device fields split them further if needed.
const VIMEO_STALL_SURFACE = 'guestHeaderVideoBackground';

type Video = Readonly<{
  isVimeo: false;
  isYoutube: false;
  videoUrl: string;
}>;

type YoutubeVideo = Readonly<{
  isYoutube: true;
  videoId: string;
  videoUrl: string;
}>;

type VimeoVideo = Readonly<{
  isVimeo: true;
  videoUrl: string;
}>;

type PreparedVideo = Video | YoutubeVideo | VimeoVideo;

export const VideoBackgroundRenderer: FC<VideoBackgroundRendererProps> = props => {
  const { tq } = useTranslation('videoBackground');
  const {
    videoUrl,
    showPlaceholder = false,
    skipAnimation,
    thumbnailUrl,
    height,
    isCompactView,
    isMobile,
    currentPageType,
    pageSlug,
    handleEditVideoClick,
    editActionVerticalAlign = 'center',
    stallButtonPlacement = 'bottomRight',
    children,
  } = props;
  const { pageColor } = useColorPaletteProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const isPreviewing = isInIframe();
  // Narrower than isPreviewing: Carbon embeds and masked domains are framed but are real guests.
  const isAdminPreview = useIsAdminGuestSitePreview();
  const [isTapToPlayEnabled] = useIsFeatureEnabled('websiteVideoTapToPlayEnabled');
  // Kill switch: off omits stallDetection, so the hook never stalls (no button) and sends no telemetry.
  const vimeoStallDetection = useMemo<VimeoStallDetection | undefined>(
    () => (isTapToPlayEnabled ? { surface: VIMEO_STALL_SURFACE, isPreview: isAdminPreview } : undefined),
    [isTapToPlayEnabled, isAdminPreview],
  );
  const { toast } = useToast();

  // A video swap without remount (desktop/mobile URL flip, admin edit) must
  // re-arm the reveal: stale isLoading=false would show the new player while
  // it buffers black, and a stale error would keep the new video hidden.
  // Layout effect so the reset lands before paint, not one frame late.
  useIsomorphicLayoutEffect(() => {
    setIsLoading(true);
    setError(false);
  }, [videoUrl]);

  // 16:9 landscape for desktop, 3:4 portrait for mobile
  const videoMinWidth = isMobile ? '75vh' : '177.78vh';
  const videoMinHeight = isMobile ? '133.33vw' : '56.25vw';

  const handleVideoPlaying = useEventCallback(() => {
    setIsLoading(false);
    // A Vimeo error can precede real playback (iOS blocking autoplay, then a tap to play);
    // once frames move the video is fine, so a sticky error would hide it for no reason.
    setError(false);
  });

  const handleIframeError = useEventCallback(() => {
    setIsLoading(false);
    setError(true);
  });

  const preparedVideo: PreparedVideo | null = useMemo(() => {
    if (!videoUrl) {
      return null;
    }

    try {
      if (isVimeoUrl(videoUrl)) {
        const embedUrl = buildVimeoBackgroundEmbedUrl(videoUrl);
        if (!embedUrl) return null;
        const video: VimeoVideo = {
          isVimeo: true,
          videoUrl: embedUrl,
        };

        return video;
      }
      if (isYoutubeUrl(videoUrl)) {
        const videoId = extractYoutubeVideoId(videoUrl);

        if (!videoId) {
          return null;
        }

        const video: YoutubeVideo = {
          isYoutube: true,
          videoId,
          videoUrl,
        };

        return video;
      }

      const video: Video = {
        isVimeo: false,
        isYoutube: false,
        videoUrl,
      };

      return video;
    } catch (error) {
      if (isPreviewing) {
        toast(tq`We couldn't load your video background.`);
      }

      addError(error, { context: 'VideoBackgroundRenderer#preparedVideoUrl' });

      return null;
    }
  }, [videoUrl, isPreviewing, toast, tq]);

  const isVimeoEmbed = !!preparedVideo && 'isVimeo' in preparedVideo && preparedVideo.isVimeo;

  // Vimeo reveals on the player's 'playing' event, not iframe load — see the hook doc.
  const {
    iframeRef: vimeoIframeRef,
    stallStatus,
    playAfterStall,
  } = useVimeoPlaybackSignal({
    enabled: isVimeoEmbed,
    videoKey: preparedVideo?.videoUrl,
    onPlaying: handleVideoPlaying,
    onError: handleIframeError,
    stallDetection: vimeoStallDetection,
  });

  const showVideo = !isLoading && !error && !!preparedVideo;
  // Not gated on `error`: with autoplay blocked, Vimeo can fire `error` instead of stalling quietly,
  // and that guest needs the button most. `failed` drops it and leaves the poster, as before it existed.
  // Compact view collapses the frame too small to hold it without covering the nav.
  const showStallButton = (stallStatus === 'stalled' || stallStatus === 'retrying') && !isCompactView;

  return (
    <AnimatedImageBox
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${CONTAINER_ASPECT_RATIO}`}
      overflow="hidden"
      backgroundColor={skipAnimation ? 'transparent' : showPlaceholder ? 'lightgray' : pageColor}
      skipAnimation={skipAnimation}
      minHeight={pxToRem(96)}
    >
      <InlineEditor
        elementLabel={tq`Video`}
        wrapperType="actionInside"
        actionData={{ editVideo: handleEditVideoClick }}
        componentName="headerVideo"
        pageName={currentPageType}
        pageSlug={pageSlug || ''}
        wrapperCSS={{ width: '100%', height: '100%' }}
        actionInsideVerticalAlign={editActionVerticalAlign}
        isEligibleForInlineEditing={!isCompactView}
      >
        {thumbnailUrl && (
          <Box
            as="img"
            src={thumbnailUrl}
            alt={tq`Video Background Thumbnail`}
            position="absolute"
            opacity={showVideo ? 0 : 1}
            transition="opacity 0.3s ease-in-out"
            top={0}
            left={0}
            width="100%"
            height="100%"
            objectFit="cover"
            zIndex={1}
          />
        )}

        {!!preparedVideo &&
          ('isYoutube' in preparedVideo && preparedVideo.isYoutube ? (
            <YoutubeVideoContainer
              videoId={preparedVideo.videoId}
              videoUrl={preparedVideo.videoUrl}
              showVideo={showVideo}
              onPlaying={handleVideoPlaying}
              onError={handleIframeError}
              minWidth={videoMinWidth}
              minHeight={videoMinHeight}
            />
          ) : (
            <Box
              as="iframe"
              ref={vimeoIframeRef}
              title={tq`Video Background`}
              src={preparedVideo.videoUrl}
              allow="autoplay; fullscreen; picture-in-picture"
              position="absolute"
              top="50%"
              left="50%"
              border="none"
              outline="none"
              transform="translate(-50%, -50%)"
              opacity={showVideo ? 1 : 0}
              transition="opacity 0.3s ease-in-out"
              onLoad={isVimeoEmbed ? undefined : handleVideoPlaying}
              onError={handleIframeError}
              width="100vw"
              height="100vh"
              minWidth={videoMinWidth}
              minHeight={videoMinHeight}
              zIndex={1}
            />
          ))}
      </InlineEditor>

      <Box
        width="100%"
        height="100%"
        position={isCompactView ? 'relative' : 'absolute'}
        zIndex="banner"
        pointerEvents={isPreviewing ? 'none' : 'auto'}
        top={0}
      >
        {children}
      </Box>

      {showStallButton && (
        <VideoStallPlayButton
          placement={stallButtonPlacement}
          isRetrying={stallStatus === 'retrying'}
          onPlay={playAfterStall}
        />
      )}
    </AnimatedImageBox>
  );
};

export const VideoBackgroundLayout: FC<VideoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    showPlaceholder = false,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const { isCompactView } = useIsCompactBrannanHeader({
    pages,
    pageSlug,
    currentPageType,
    brannanNavigation: restProps.eventDesign?.brannanNavigation,
  });

  const isMobileScreen = useIsMobileScreen();
  const skipAnimation = useSkipInitialAnimation();

  const hideVideoArgs = {
    isCompactView,
    navBarVariant: restProps.navBarVariant,
    showVideoOnlyOnWelcomePage: websiteHeader.headerPhoto.showVideoOnlyOnWelcomePage,
  };
  const shouldHideVideoBackground = shouldHideStandaloneVideo(hideVideoArgs);
  const isOverlayNav = shouldOverlayStandaloneNav(hideVideoArgs);

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            // The chevron's 90svh applies below `sm2` (768 — where useIsMobileScreen actually
            // flips: breakpointsByScreen starts 'tablet' at theme.breakpoints[1]) via a
            // responsive prop (CSS media query), not the isMobileScreen JS value — a hydration
            // flip of the UA guess re-sized the frame. The boundary must match the hook, which
            // still drives the chevron itself.
            height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : { _: '90svh', sm2: '100svh' },
            aspectRatio: `${CONTAINER_ASPECT_RATIO}`,
          },
    [isCompactView, isInlineEditing, renderedContainerHeight],
  );

  const viewportHeightFallback = isMobileScreen ? '90svh' : '100svh';

  const { video, headerTitleStyle, navigationStyle } = websiteHeader.headerPhoto;
  const videoUrl = isMobileScreen && video?.mobileUrl ? video.mobileUrl : video?.desktopUrl;

  // The couple can pin the event name to the bottom-right corner; move the stall button to the
  // opposite corner then. Mirrors the title's own fallback from pageAlignment.x to textAlignment.
  const eventName = websiteHeader.headerPhoto.eventName;
  const isTitleBottomRight = eventName?.pageAlignment?.y === 'bottom' && (eventName.pageAlignment.x || eventName.textAlignment) === 'right';
  const stallButtonPlacement: VideoStallButtonPlacement = isTitleBottomRight ? 'bottomLeft' : 'bottomRight';

  // Memoize container dimensions to prevent unnecessary style recalculations
  // when hiding video background on mobile/tablet
  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHideVideoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHideVideoBackground ? 'auto' : height,
      containerMinHeight: shouldHideVideoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHideVideoBackground ? 'visible' : 'hidden',
    };
  }, [shouldHideVideoBackground, aspectRatio, height]);

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: { ...props, placeholderImageUrl: video?.placeholderImageUrl }, validationConstraints: { maxLength: EVENT_DISPLAY_NAME_MAX_LENGTH } },
      },
    });
  });

  const handleEditVideoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderVideo' },
    });
  });

  return (
    <VideoBackgroundOuter
      position="relative"
      width="100%"
    >
      <Flex
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHideVideoBackground ? (
          <EventNameNavForStandaloneHeader
            eventDisplayName={websiteHeader.eventDisplayName}
            colors={websiteHeader.headerPhoto.colors}
            headerTextData={websiteHeader.headerPhoto.eventName}
            isCompactView={isCompactView}
            currentPageType={currentPageType}
            handleEditTextClick={handleEditTextClick}
            isContentEditable={isInlineEditing}
            editableContentRef={editableContentRef}
            forceRenderViewIn={forceRenderViewIn}
            scaleValue={scaleValue}
            pages={pages}
            pageSlug={pageSlug}
            renderedContainerHeight={renderedContainerHeight}
            renderedContainerWidth={renderedContainerWidth}
            viewportHeightFallback={viewportHeightFallback}
            headerTitleStyle={headerTitleStyle}
            navigationStyle={navigationStyle}
            isBackgroundHidden
            isOverlayNav={isOverlayNav}
            {...restProps}
          />
        ) : (
          <VideoBackgroundRenderer
            showPlaceholder={showPlaceholder}
            skipAnimation={skipAnimation}
            videoUrl={videoUrl}
            thumbnailUrl={video?.placeholderImageUrl}
            // Fills the CSS-sized Flex frame above — keeps the renderer's height a plain string.
            height="100%"
            isCompactView={isCompactView}
            isMobile={isMobileScreen && !!video?.mobileUrl}
            currentPageType={currentPageType}
            pageSlug={pageSlug}
            handleEditVideoClick={handleEditVideoClick}
            stallButtonPlacement={stallButtonPlacement}
          >
            <EventNameNavForStandaloneHeader
              eventDisplayName={websiteHeader.eventDisplayName}
              colors={websiteHeader.headerPhoto.colors}
              headerTextData={websiteHeader.headerPhoto.eventName}
              isCompactView={isCompactView}
              currentPageType={currentPageType}
              handleEditTextClick={handleEditTextClick}
              isContentEditable={isInlineEditing}
              editableContentRef={editableContentRef}
              forceRenderViewIn={forceRenderViewIn}
              scaleValue={scaleValue}
              pages={pages}
              pageSlug={pageSlug}
              renderedContainerHeight={renderedContainerHeight}
              renderedContainerWidth={renderedContainerWidth}
              viewportHeightFallback={viewportHeightFallback}
              headerTitleStyle={headerTitleStyle}
              navigationStyle={navigationStyle}
              {...restProps}
            />
          </VideoBackgroundRenderer>
        )}
      </Flex>
      {isMobileScreen && !isCompactView && !shouldHideVideoBackground && !isInlineEditing && <BouncingChevron />}
    </VideoBackgroundOuter>
  );
};

VideoBackgroundLayout.displayName = 'VideoBackgroundLayout';
